/*
 * Copyright (c) 2026. Orion Analytics. All Rights Reserved
 */

import React, { useEffect } from "react";
import NAV from "./nav";
import Carousel from "./carousel";


const FundExplorerStandalone = () => {

  useEffect(() => {
    // Tailwind CDN (same as Stitch)
    const script = document.createElement("script");
    script.src = "https://cdn.tailwindcss.com?plugins=forms,container-queries";
    script.async = true;
    document.head.appendChild(script);

    script.onload = () => {
      window.tailwind.config = {
        darkMode: "class",
        theme: {
          extend: {
            colors: {
              primary: "#006e25",
              "primary-container": "#28a745",
              surface: "#f8f9fa",
              "surface-container": "#edeeef",
              "surface-container-low": "#f3f4f5",
              "surface-container-high": "#e7e8e9",
              "surface-container-lowest": "#ffffff",
              "on-surface": "#191c1d",
              "on-surface-variant": "#3e4a3c",
              secondary: "#346669",
              outline: "#6e7b6b",
              error: "#ba1a1a",
            },
            fontFamily: {
              headline: ["Manrope"],
              body: ["Inter"],
            },
            borderRadius: {
              xl: "0.5rem",
              "2xl": "1rem",
              "3xl": "1.5rem",
            },
          },
        },
      };
    };

    // Fonts
    const fonts = document.createElement("link");
    fonts.href =
      "https://fonts.googleapis.com/css2?family=Manrope:wght@400;700;800&family=Inter:wght@300;400;500;600&display=swap";
    fonts.rel = "stylesheet";
    document.head.appendChild(fonts);

    // Icons
    const icons = document.createElement("link");
    icons.href =
      "https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined";
    icons.rel = "stylesheet";
    document.head.appendChild(icons);

  }, []);

  return (
    <div className="bg-surface text-on-surface font-body min-h-screen">
      <div className="container-fluid position-relative p-0">
        <NAV activetag="mf"/>
        <Carousel activetag="mf"/>
        {/*<!-- Header Start -->*/}
        <div className="container-fluid bg-breadcrumb">
          <div className="container text-center py-5" style={{maxwidth: '500px'}}>
            <h4 className="text-white display-4 mb-4 wow fadeInDown"
                data-wow-delay="0.1s">Mutual Funds</h4>

          </div>
        </div>
      </div>

      {/* 🔥 TOP NAV */}
      <header className="sticky top-0 z-50 bg-surface/80 backdrop-blur-lg border-b border-outline/10">
        <div className="flex items-center justify-between px-6 py-4 max-w-[1600px] mx-auto">

          <div className="flex items-center gap-8">
            <span className="text-xl font-extrabold text-primary">
              Orion Analytics
            </span>

            <nav className="hidden md:flex items-center gap-6 text-sm">
              <a className="text-on-surface-variant hover:text-primary">Dashboard</a>
              <a className="text-primary font-semibold border-b-2 border-primary pb-1">
                Fund Explorer
              </a>
              <a className="text-on-surface-variant hover:text-primary">Market Insights</a>
              <a className="text-on-surface-variant hover:text-primary">Portfolio</a>
            </nav>
          </div>

          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center bg-surface-container-low px-4 py-2 rounded-full">
              <span className="material-symbols-outlined text-sm mr-2">search</span>
              <input
                className="bg-transparent outline-none text-sm"
                placeholder="Search funds..."
              />
            </div>
          </div>

        </div>
      </header>

      <div className="flex max-w-[1600px] mx-auto">

        {/* 🔥 SIDEBAR */}
        <aside className="hidden lg:flex flex-col w-[260px] px-4 py-6 bg-surface-container-low border-r border-outline/10">

          <h2 className="text-sm font-semibold text-primary mb-4">
            Investor Portal
          </h2>

          <div className="space-y-1 text-sm">

            <div className="flex items-center gap-3 px-4 py-2 rounded-xl hover:bg-surface-container cursor-pointer">
              <span className="material-symbols-outlined text-sm">dashboard</span>
              Dashboard
            </div>

            <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-surface-container text-primary font-semibold">
              <span className="material-symbols-outlined text-sm">search</span>
              Fund Explorer
            </div>

            <div className="flex items-center gap-3 px-4 py-2 rounded-xl hover:bg-surface-container cursor-pointer">
              <span className="material-symbols-outlined text-sm">insights</span>
              Insights
            </div>

            <div className="flex items-center gap-3 px-4 py-2 rounded-xl hover:bg-surface-container cursor-pointer">
              <span className="material-symbols-outlined text-sm">account_balance</span>
              Portfolio
            </div>

          </div>

        </aside>

        {/* 🔥 MAIN */}
        <main className="flex-1 px-6 py-8">

          {/* HEADER BENTO */}
          <section className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">

            <div className="md:col-span-2 bg-primary text-white p-8 rounded-3xl">
              <h1 className="text-3xl font-bold font-headline">
                Fund Universe Explorer
              </h1>
              <p className="mt-2 opacity-90">
                Analyze 2,450+ funds with institutional-grade analytics
              </p>
            </div>

            <div className="bg-surface-container-low p-6 rounded-3xl">
              <p className="text-sm text-on-surface-variant">Top Category</p>
              <p className="text-lg font-semibold mt-1">Small Cap</p>
            </div>

          </section>

          <div className="flex gap-6">

            {/* 🔥 FILTER PANEL */}
            <aside className="w-[280px] space-y-6">

              <div className="bg-surface-container-low p-6 rounded-3xl">
                <h3 className="text-lg font-semibold mb-4">
                  Filters
                </h3>

                <div className="space-y-3 text-sm">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" /> Equity
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" /> Debt
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" /> Hybrid
                  </label>
                </div>
              </div>

            </aside>

            {/* 🔥 TABLE */}
            <div className="flex-1">

              <h2 className="text-lg font-semibold mb-4">
                124 Funds Found
              </h2>

              <div className="overflow-hidden rounded-2xl border border-outline/10">
                <table className="w-full text-sm">

                  <thead className="bg-surface-container-low text-left">
                    <tr>
                      <th className="px-6 py-4">Fund</th>
                      <th className="px-6 py-4">Category</th>
                      <th className="px-6 py-4">1Y Return</th>
                      <th className="px-6 py-4">Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr className="border-t border-outline/10">
                      <td className="px-6 py-4 font-medium">SBI Small Cap Fund</td>
                      <td className="px-6 py-4">Small Cap</td>
                      <td className="px-6 py-4 text-green-600">26.4%</td>
                      <td className="px-6 py-4">
                        <button className="bg-primary text-white px-4 py-2 rounded-full text-sm">
                          Invest
                        </button>
                      </td>
                    </tr>
                  </tbody>

                </table>
              </div>

            </div>

          </div>

        </main>

      </div>

      {/* 🔥 MOBILE NAV */}
      <div className="fixed bottom-0 left-0 right-0 bg-surface-container-low border-t border-outline/10 flex justify-around py-3 md:hidden text-xs">

        <div className="flex flex-col items-center text-on-surface-variant">
          <span className="material-symbols-outlined">home</span>
          Home
        </div>

        <div className="flex flex-col items-center text-primary">
          <span className="material-symbols-outlined">search</span>
          Explore
        </div>

        <div className="flex flex-col items-center text-on-surface-variant">
          <span className="material-symbols-outlined">insights</span>
          Insights
        </div>

        <div className="flex flex-col items-center text-on-surface-variant">
          <span className="material-symbols-outlined">account_balance</span>
          Holdings
        </div>

      </div>

    </div>
  );
};

export default FundExplorerStandalone;